// AI Config → Lara · Quote scope: who/what Lara runs on + her knowledge sources.
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Sparkles, Save, Loader2, Upload, FileText, Plus } from "lucide-react";
import api, { formatApiError } from "../../lib/api";

const inp = "w-full rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2 text-[13px] text-white focus:outline-none focus:border-violet-400/60";
const lbl = "block text-[10.5px] uppercase tracking-widest text-slate-500 font-mono mb-1.5";

function Chips({ all, value, onChange, testid }) {
  const on = (x) => value.includes(x);
  return (
    <div className="flex flex-wrap gap-1.5" data-testid={testid}>
      {all.map((x) => (
        <button key={x} type="button" onClick={() => onChange(on(x) ? value.filter((v) => v !== x) : [...value, x])}
          className={`px-2.5 py-1 rounded-full text-[11.5px] border ${on(x) ? "border-violet-400/60 bg-violet-500/15 text-violet-200" : "border-white/10 text-slate-400"}`}>{x}</button>
      ))}
    </div>
  );
}

function KnowledgeDocs() {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [paste, setPaste] = useState({ title: "", content: "" });
  const fileRef = useRef(null);
  const load = () => api.get("/lara/kb/docs").then(({ data }) => setItems(data.items || [])).catch(() => {});
  useEffect(() => { load(); }, []);
  const upload = async (f) => {
    if (!f) return;
    const fd = new FormData(); fd.append("file", f);
    setBusy(true);
    try { const { data } = await api.post("/lara/kb/upload", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 120000 }); toast.success(`Added "${data.title}" (${data.chars.toLocaleString()} chars)`); load(); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };
  const addText = async () => {
    try { await api.post("/lara/kb/text", paste); setPaste({ title: "", content: "" }); toast.success("Knowledge added"); load(); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };
  const toggle = async (it) => { await api.patch(`/lara/kb/docs/${it.id}`, { is_active: !it.is_active }); load(); };
  const remove = async (it) => { if (!window.confirm(`Delete "${it.title}" from Lara's knowledge?`)) return; await api.delete(`/lara/kb/docs/${it.id}`); load(); };
  return (
    <div className="space-y-3" data-testid="lara-kb">
      <div className="flex items-center justify-between gap-2">
        <div className={lbl + " !mb-0"}>Extra knowledge for Lara (on top of the Hub)</div>
        <input ref={fileRef} type="file" accept=".pdf,.docx,.txt,.md" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid="lara-kb-file" />
        <button type="button" onClick={() => fileRef.current?.click()} disabled={busy} data-testid="lara-kb-upload"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-violet-400/40 text-violet-200 text-[12px] font-semibold hover:bg-violet-500/10 disabled:opacity-40">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />} Upload PDF / Word / TXT
        </button>
      </div>
      <div className="grid sm:grid-cols-[1fr_auto] gap-2">
        <div className="space-y-2">
          <input className={inp} placeholder="Title, e.g. Kingspan install guide" value={paste.title} onChange={(e) => setPaste((p) => ({ ...p, title: e.target.value }))} data-testid="lara-kb-title" />
          <textarea className={`${inp} resize-y`} rows={3} placeholder="Paste text Lara should know…" value={paste.content} onChange={(e) => setPaste((p) => ({ ...p, content: e.target.value }))} data-testid="lara-kb-text" />
        </div>
        <button type="button" onClick={addText} disabled={!paste.title.trim() || paste.content.trim().length < 10} data-testid="lara-kb-add-text"
          className="self-end inline-flex items-center gap-1 px-3 py-2 rounded-lg bg-violet-600 hover:bg-violet-500 disabled:opacity-40 text-white text-[12px] font-semibold"><Plus className="w-3.5 h-3.5" /> Add text</button>
      </div>
      <div className="divide-y divide-white/[0.05] rounded-lg border border-white/[0.06]">
        {items.length === 0 && <div className="px-3 py-2.5 text-[12px] text-slate-500">No extra documents yet — Lara uses the Hub + Knowledge Base.</div>}
        {items.map((it) => (
          <div key={it.id} className="px-3 py-2 flex items-center gap-2 text-[12.5px]" data-testid={`lara-kb-doc-${it.id}`}>
            <FileText className="w-3.5 h-3.5 text-slate-400 shrink-0" />
            <span className={`flex-1 truncate ${it.is_active ? "text-slate-200" : "text-slate-500 line-through"}`}>{it.title}</span>
            <span className="text-[10.5px] text-slate-500 font-mono">{it.chars.toLocaleString()} chars · {it.owner}</span>
            <button type="button" onClick={() => toggle(it)} className="text-[11px] text-slate-400 hover:text-white" data-testid={`lara-kb-toggle-${it.id}`}>{it.is_active ? "Turn off" : "Turn on"}</button>
            <button type="button" onClick={() => remove(it)} className="text-[11px] text-rose-300/80 hover:text-rose-200" data-testid={`lara-kb-delete-${it.id}`}>Delete</button>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function LaraSettingsCard() {
  const [meta, setMeta] = useState(null);
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/lara/quote/settings").then(({ data }) => { setMeta(data); setS(data.settings); }).catch(() => {}); }, []);
  if (!s) return null;
  const set = (k, v) => setS((x) => ({ ...x, [k]: v }));
  const save = async () => {
    setSaving(true);
    try { const { data } = await api.put("/lara/quote/settings", s); setS(data.settings); toast.success("Lara settings saved"); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return (
    <div className="rounded-2xl border border-violet-400/30 bg-violet-500/[0.04] p-5 space-y-5" data-testid="lara-settings-card">
      <div className="flex items-start gap-3">
        <span className="w-9 h-9 rounded-xl grid place-items-center bg-gradient-to-br from-violet-500 to-amber-500 text-white shrink-0"><Sparkles className="w-4 h-4" /></span>
        <div className="flex-1">
          <div className="text-white font-semibold">Lara AI · Quote scope</div>
          <div className="text-[12.5px] text-slate-400">Which quotes show the Lara button and what she reads. Prompts and models for Lara live in the two "Lara ·" cards above. Each future Lara area gets its own scope here.</div>
        </div>
        <label className="flex items-center gap-2 text-[12px] text-slate-300"><input type="checkbox" checked={s.enabled} onChange={(e) => set("enabled", e.target.checked)} data-testid="lara-enabled" /> On</label>
      </div>
      <div className="grid sm:grid-cols-3 gap-3">
        <div><label className={lbl}>Min quote value (£)</label><input type="number" min="0" className={inp} value={s.min_value} onChange={(e) => set("min_value", e.target.value)} data-testid="lara-min-value" /></div>
        <div><label className={lbl}>Min % filled in</label><input type="number" min="0" max="100" className={inp} value={s.min_completeness ?? 40} onChange={(e) => set("min_completeness", e.target.value)} data-testid="lara-min-completeness" /></div>
        <div><label className={lbl}>Rule</label>
          <select className={`${inp} bg-slate-900/70`} value={s.rule} onChange={(e) => set("rule", e.target.value)} data-testid="lara-rule">
            <option value="any">Value OR tier</option><option value="all">Value AND tier</option>
          </select></div>
        <div><label className={lbl}>Min customer tier</label>
          <select className={`${inp} bg-slate-900/70`} value={s.min_tier} onChange={(e) => set("min_tier", e.target.value)} data-testid="lara-min-tier">
            <option value="">No tier rule</option>{[...meta.tiers].reverse().map((t) => <option key={t} value={t}>{t[0] + t.slice(1).toLowerCase()} or above</option>)}
          </select></div>
      </div>
      <label className="flex items-center gap-2 text-[12.5px] text-slate-300"><input type="checkbox" checked={s.admins_only} onChange={(e) => set("admins_only", e.target.checked)} data-testid="lara-admins-only" /> Admins only (testing) — untick later to open Lara to the team</label>
      <div><label className={lbl}>Hub groups Lara reads (none ticked = all)</label><Chips all={meta.hub_groups} value={s.hub_groups} onChange={(v) => set("hub_groups", v)} testid="lara-hub-groups" /></div>
      <div><label className={lbl}>Knowledge Base scopes Lara reads</label><Chips all={meta.kb_scopes} value={s.kb_scopes} onChange={(v) => set("kb_scopes", v)} testid="lara-kb-scopes" /></div>
      <div><label className={lbl}>Extra house rules for Lara (added to her prompts)</label>
        <textarea className={`${inp} resize-y`} rows={3} value={s.extra_instructions} onChange={(e) => set("extra_instructions", e.target.value)} placeholder="e.g. Always offer next-day delivery check · Never discount below web price without manager OK" data-testid="lara-extra" /></div>
      <div className="flex justify-end"><button type="button" onClick={save} disabled={saving} data-testid="lara-settings-save"
        className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-violet-600 hover:bg-violet-500 disabled:opacity-40 text-white text-[13px] font-semibold">
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save Lara settings</button></div>
      <KnowledgeDocs />
    </div>
  );
}
